// Which wall a placed product belongs to. The three ways the planner answers
// that while dragging live in wallProximity.ts; reducing them to one is its
// own ticket (CO-5732 carved it out).
import * as THREE from 'three';
import {CabinetMesh, RoomType} from 'components/customer/RoomPlanner/types';
import {store} from 'store/customer';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';
import {
    getDirection,
    WALL_ROTATIONS,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {findAbuttingWall} from 'components/customer/RoomPlanner/helpers/wallProximity';

/**
 * Updates the currentWall property for each cabinet in their userData
 * @param {CabinetMesh[]} cabinets - Array of cabinet meshes to update
 * @param {THREE.Scene} scene - The scene containing the walls
 * @param {string} roomType - The type of room (RECTANGULAR or LSHAPED)
 */
export const updateCabinetsCurrentWall = (
    cabinets: CabinetMesh[],
    scene: THREE.Scene
) => {
    const state = store.getState();
    const roomType = state.roomPlanner.roomType;

    cabinets.forEach((cabinet) => {
        const wallBehind = getWallBehindCabinet(cabinet, scene, roomType);

        cabinet.userData.currentWall = wallBehind as THREE.Mesh | null;
    });
};

// Helper function to get closest wall based on cabinet position
const getClosestWallByPosition = (
    cabinetPosition: THREE.Vector3,
    wallGroup: THREE.Group
): string => {
    if (!wallGroup) return '';

    const walls = wallGroup.children.filter(
        (child) => child.name.includes('wall:') || child.name.includes('Wall')
    );
    let closestWall = '';
    let minDistance = Infinity;

    walls.forEach((wall) => {
        const wallMesh = wall as THREE.Mesh;
        const wallPosition = wallMesh.position;

        // Calculate distance from cabinet to wall
        const distance = cabinetPosition.distanceTo(wallPosition);

        if (distance < minDistance) {
            minDistance = distance;
            closestWall = wall.name;
        }
    });

    return closestWall;
};

export const getWallBehindCabinet = (
    cabinetGroup: THREE.Group<THREE.Object3DEventMap>,
    scene: THREE.Scene,
    roomType: RoomType
) => {
    let wallBehind = '';

    const wallGroup = scene.children.find(
        (ch) =>
            ch.name === ObjectNameMap.rectangularWall ||
            ch.name === ObjectNameMap.lShapeWall
    ) as THREE.Group;

    const direction = getDirection(cabinetGroup.rotation.z);

    if (roomType === 'LSHAPED') {
        const abuttingWall = findAbuttingWall(cabinetGroup, wallGroup)?.name;
        const abuttingFacesTheSameWay =
            !!abuttingWall &&
            abuttingWall in WALL_ROTATIONS &&
            getDirection(
                WALL_ROTATIONS[abuttingWall as keyof typeof WALL_ROTATIONS]
            ) === direction;

        if (abuttingFacesTheSameWay) {
            wallBehind = abuttingWall;
        } else {
            switch (direction) {
                case 'normal':
                    wallBehind = 'rightWidthWall';
                    break;
                case 'back':
                    wallBehind = 'leftDepthWall';
                    break;
                case 'right':
                    wallBehind = 'leftWidthWall';
                    break;
                case 'left':
                    wallBehind = 'rightDepthWall';
                    break;
            }
        }
    } else {
        // Use position-based wall detection instead of rotation
        wallBehind = getClosestWallByPosition(cabinetGroup.position, wallGroup);
    }

    if (!wallBehind) return null;

    const closestWall = (wallGroup.parent as THREE.Scene).getObjectByName(
        wallBehind
    ) as THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial>;

    return closestWall;
};
